import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

interface Notification {
  id: number;
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  action_required: boolean;
  action_data?: any;
  created_at: string;
  read: boolean;
}

interface NotificationCenterProps {
  onNotificationClick?: (notification: Notification) => void;
}

// Derive the WebSocket URL from the REST API base URL.
const getWsUrl = (token: string): string => {
  let base = process.env.REACT_APP_API_URL;
  if (!base) {
    base = window.location.hostname !== 'localhost'
      ? `http://${window.location.hostname}:8000`
      : 'http://localhost:8000';
  }
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/ws/notifications?token=${encodeURIComponent(token)}`;
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onNotificationClick }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const wsConnected = useRef(false);
  const closedByUnmount = useRef(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications/');
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 404) {
        setNotifications([{
          id: 1,
          type: 'info',
          title: 'Notification System',
          message: 'Notification system is being set up. Backend endpoints will be available soon.',
          action_required: false,
          created_at: new Date().toISOString(),
          read: false
        }]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Merge a pushed notification into the list (dedupe by id, newest first).
  const mergeNotification = useCallback((incoming: Notification) => {
    setNotifications((prev) => {
      if (prev.some((n) => n.id === incoming.id)) return prev;
      return [incoming, ...prev];
    });
  }, []);

  // Establish the realtime WebSocket connection with reconnect backoff.
  const connectWs = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(getWsUrl(token));
    } catch (e) {
      console.warn('WebSocket init failed, relying on polling:', e);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      wsConnected.current = true;
      reconnectAttempts.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.event === 'notification' && data.notification) {
          mergeNotification(data.notification as Notification);
        }
      } catch {
        /* ignore malformed frames */
      }
    };

    ws.onclose = () => {
      wsConnected.current = false;
      wsRef.current = null;
      if (closedByUnmount.current) return;
      // Exponential backoff, capped at 30s.
      const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts.current));
      reconnectAttempts.current += 1;
      reconnectRef.current = setTimeout(connectWs, delay);
    };

    ws.onerror = () => {
      // Let onclose handle reconnect.
      try { ws.close(); } catch { /* noop */ }
    };
  }, [mergeNotification]);

  useEffect(() => {
    closedByUnmount.current = false;
    // Initial load, then realtime updates via WebSocket.
    fetchNotifications();
    connectWs();

    // Fallback poll: only fetches when the WebSocket is NOT connected, so a
    // healthy realtime connection means no polling. This is the safety net for
    // environments where WebSockets are blocked (e.g. some proxies).
    const fallback = setInterval(() => {
      if (!wsConnected.current) {
        fetchNotifications();
      }
    }, 30000);

    return () => {
      closedByUnmount.current = true;
      clearInterval(fallback);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
      }
    };
  }, [fetchNotifications, connectWs]);

  const markAsRead = async (notificationId: number) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const actionRequiredCount = notifications.filter(n => n.action_required && !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      case 'info': return 'ℹ️';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success': return '#16a34a';
      case 'warning': return '#f59e0b';
      case 'error': return '#dc2626';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-center">
      <button
        className="notification-bell"
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          position: 'relative'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: actionRequiredCount > 0 ? '#dc2626' : '#3b82f6',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div 
            className="modal-overlay"
            onClick={() => setShowDropdown(false)}
          />
          <div className="modal">
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              Notifications
            </h3>
            <button
              onClick={() => setShowDropdown(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '18px'
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    background: notification.read ? 'white' : '#f0f9ff',
                    borderLeft: `4px solid ${getNotificationColor(notification.type)}`,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = notification.read ? '#f8f9fa' : '#e0f2fe';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.read ? 'white' : '#f0f9ff';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: notification.read ? '500' : '600',
                        fontSize: '14px',
                        color: '#1e293b',
                        marginBottom: '4px'
                      }}>
                        {notification.title}
                        {notification.action_required && (
                          <span style={{
                            marginLeft: '8px',
                            background: '#fbbf24',
                            color: '#92400e',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            Action Required
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '13px',
                        color: '#64748b',
                        lineHeight: '1.4',
                        marginBottom: '4px'
                      }}>
                        {notification.message}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {formatTimeAgo(notification.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8f9fa',
                textAlign: 'center'
              }}
            >
              <button
                onClick={() => {
                  // TODO: Navigate to full notifications page
                  setShowDropdown(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;